import MapComponent from "./MapComponent";
import "./App.css";
import { Button, Layout, List, Select, Modal, Popover, Drawer, Spin } from "antd";
import Sider from "antd/es/layout/Sider";
import { Content } from "antd/es/layout/layout";
import { FC, useEffect, useState } from "react";
import { debounce } from "lodash";
import Filters from "./Filters";
import { SearchOutlined, DoubleRightOutlined, LoadingOutlined } from "@ant-design/icons";
import SMSRegisterForm from "./SMSRegisterForm";
import axios from "axios";
import googleMapsService from "./services/googleMapsService";
import { isWithinGenevaCanton } from "./utils/genevaPolygon";

// Utility function to parse URL hash parameters
const parseUrlHash = (): { lat?: number; lon?: number } => {
  const hash = window.location.hash;
  const params: { lat?: number; lon?: number } = {};
  
  if (hash) {
    // Support both #lat=46.12345#lon=34.1321321 and #lat=46.12345&lon=34.1321321 formats
    const hashParts = hash.substring(1).split(/[#&]/);
    
    hashParts.forEach(part => {
      const [key, value] = part.split('=');
      if (key === 'lat' && value) {
        const lat = parseFloat(value);
        if (!isNaN(lat) && lat >= -90 && lat <= 90) {
          params.lat = lat;
        }
      } else if (key === 'lon' && value) {
        const lon = parseFloat(value);
        if (!isNaN(lon) && lon >= -180 && lon <= 180) {
          params.lon = lon;
        }
      }
    });
  }
  
  return params;
};

// Utility function to update URL hash with coordinates
const updateUrlHash = (lat: number, lon: number) => {
  const newHash = `#lat=${lat}#lon=${lon}`;
  if (window.location.hash !== newHash) {
    window.history.replaceState(null, '', newHash);
  }
};

const elementInViewport = (el: any) => {
  var top = el.offsetTop;
  var left = el.offsetLeft;
  var width = el.offsetWidth;
  var height = el.offsetHeight;

  while (el.offsetParent) {
    el = el.offsetParent;
    top += el.offsetTop;
    left += el.offsetLeft;
  }

  return (
    top >= window.pageYOffset &&
    left >= window.pageXOffset &&
    top + height <= window.pageYOffset + window.innerHeight &&
    left + width <= window.pageXOffset + window.innerWidth
  );
};

export const displayPrice = (value: any) =>
  value
    ? `CHF ${Math.floor(value)
        .toString()
        .replace(/\B(?=(\d{3})+(?!\d))/g, " ")}`
    : "-";
export const displayNumber = (value: any) =>
  value ? `${value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ")}` : "-";
export const displayStatus = (value: boolean) => (
  <div
    style={{
      width: 12,
      minWidth: 12,
      height: 12,
      minHeight: 12,
      borderRadius: 12,
      backgroundColor: value ? "#7ED957" : "#001484",
      // backgroundColor: "#7ED957",
      marginTop: 4,
    }}
  ></div>
);

const displayContactUsModal = () => {
  console.log("CHILD Modal open");
  const message = {
    action: "openModal",
    modalId: "#myModal", // This should match the id of the modal in the parent.
  };

  // Use postMessage to send the message to the parent.
  // "*" can be replaced with a specific origin for added security.
  if (window.self !== window.top) {
    console.log(1);
    window.parent.postMessage(message, "*");
  } else {
    console.log(2);
    window
      .open("https://transaction-map.ch/nous-contacter/", "_blank")
      ?.focus();
  }
};

const prices = ["Prix", "Prix_m2_habitable"];
const numbers = [
  "SVB_Verifiee",
  "SVB_Estimee",
  // "AnneeConstruction",
  "Etage",
  "Pieces",
  "Terrain"
];

const defaultFields = [
  "Prix",
  "Prix_m2_habitable",
  "Pieces",
  "SVB_Verifiee",
  "SVB_Estimee",
  "AnneeConstruction",
  "Etage",
  "Terrain",
];

const sidebarInfo: Record<string, string[]> = {
  Appartement: [
    "Prix",
    "Prix_m2_habitable",
    "Pieces",
    "SVB_Verifiee",
    "Etage",
    "AnneeConstruction",
  ],
  "Habitation à un seul logement": [
    "Prix",
    "Prix_m2_habitable",
    "Pieces",
    "SVB_Verifiee",
    "Terrain",
    "AnneeConstruction",
  ],
  Terrain: ["Prix", "Terrain"],
  Garage: ["Prix", "AnneeConstruction"],
  Parking: ["Prix"],
};

const fieldLabels: Record<string, string> = {
  Prix: "Prix",
  Prix_m2_habitable: "Prix/m² habitable",
  Pieces: "Nombre de pièces",
  SVB_Verifiee: "m² habitables",
  SVB_Estimee: "SVB Estimée",
  AnneeConstruction: "Année de construction",
  Terrain: "m² terrain",
  Etage: "Etage",
};

function App() {
  const [parcelles, setParcelles] = useState<any[]>([]);
  const [selectedParcelle, setSelectedParcelle] = useState<string | null>(null);
  const [selectedParcelleNoCommune, setSelectedParcelleNoCommune] = useState<string | null>(null);
  const [address, setAddress] = useState<string | null>(null);

  const [searchResult, setSearchResult] = useState<any[]>([]);
  const [center, setCenter] = useState("6.1432|46.2044");
  const [recenterMap, setRecenterMap] = useState(false);
  const [marker, setMarker] = useState("|");
  const [filters, setFilters] = useState({});
  const [checkedTransactions, setCheckedTransactions] = useState<any[]>([]);

  const [openTransactionList, setOpenTransactionList] = useState(false);
  // In iframe mode (desktop), filters should be open by default
  // In standalone mode, filters should be closed by default
  const [openFilters, setOpenFilters] = useState(window.self !== window.top);

  // Initialize map location from URL or geolocation
  useEffect(() => {
    const initializeLocation = async () => {
      // First, check if URL contains coordinates
      const urlParams = parseUrlHash();
      
      if (urlParams.lat && urlParams.lon) {
        console.log('URL coordinates found:', urlParams.lat, urlParams.lon);
        // Check if coordinates are within Geneva canton
        try {
          const isInGeneva = await isWithinGenevaCanton(urlParams.lon, urlParams.lat);
          if (isInGeneva) {
            console.log('URL coordinates are within Geneva canton, using them');
            setCenter(`${urlParams.lon}|${urlParams.lat}`);
            setMarker(`${urlParams.lon}|${urlParams.lat}`);
            setRecenterMap(true);
            return; // Exit early, don't request user location
          } else {
            console.log('URL coordinates are outside Geneva canton, falling back to geolocation');
          }
        } catch (error) {
          console.error('Error checking Geneva canton boundary for URL coordinates:', error);
        }
      }

      // If no valid URL coordinates, try geolocation
      const requestUserLocation = () => {
        if (navigator.geolocation) {
          navigator.geolocation.getCurrentPosition(
            async (position) => {
              const { longitude, latitude } = position.coords;
              console.log('User location obtained:', longitude, latitude);
              
              // Check if user is within Geneva canton using official KML boundary data
              try {
                const isInGeneva = await isWithinGenevaCanton(longitude, latitude);
                if (isInGeneva) {
                  console.log('User is within Geneva canton, centering map on their location');
                  setCenter(`${longitude}|${latitude}`);
                  setRecenterMap(true);
                  // Update URL with user's location
                  updateUrlHash(latitude, longitude);
                } else {
                  console.log('User is outside Geneva canton, keeping default Geneva location');
                  // Keep default Geneva location - no need to update center state
                }
              } catch (error) {
                console.error('Error checking Geneva canton boundary:', error);
                console.log('Using default Geneva location due to boundary check error');
              }
            },
            (error) => {
              console.log('Geolocation error:', error.message);
              // User denied location or other error, keep default Geneva location
              console.log('Using default Geneva location');
            },
            {
              enableHighAccuracy: true,
              timeout: 10000,
              maximumAge: 600000 // 10 minutes
            }
          );
        } else {
          console.log('Geolocation is not supported by this browser');
          // Keep default Geneva location
        }
      };

      requestUserLocation();
    };

    // Initialize location when app loads
    initializeLocation();
  }, []);

  const handleSMSModalCancel = () => {
    setIsSMSModalOpen(false);
    setShowConfirmForm(false);
    setSelectedParcelle(null);
    setSelectedParcelleNoCommune(null);
    setSearchLocationAuthenticated(0);
  };

  const [isSMSModalOpen, setIsSMSModalOpenCaller] = useState(false);
  const [searchLocationAuthenticated, setSearchLocationAuthenticated] =
    useState(0);

  const [showConfirmForm, setShowConfirmForm] = useState(false);

  const setIsSMSModalOpen = (value: boolean) => {
    setIsSMSModalOpenCaller(value);
    console.log(selectedParcelle);
  };

  const showSMSModal = (record_id: number) => {
    setSearchLocationAuthenticated(record_id);
    setIsSMSModalOpen(true);
  };

  const [smsToken, setSMSToken] = useState(undefined);

  const [remainingCredits, setRemainingCredits] = useState(undefined);

  // const [parcellesList, setParcellesList] = useState<any>();
  const [loadingParcellesList, setLoadingParcellesList] = useState<boolean>(true);

  useEffect(() => {
    selectedParcelle && scrollToItem(selectedParcelle);
  }, [parcelles, selectedParcelle]);

  useEffect(() => {
    const searchAddress = async (address: string) => {
      try {
        const results = await googleMapsService.searchAddress(address);
        setSearchResult(results);
      } catch (error) {
        console.error('Error searching address:', error);
        setSearchResult([]);
      }
    };

    if (address) searchAddress(address);
  }, [address]);

  const scrollToItem = (itemId: string) => {
    const element = document.getElementById(itemId);

    if (element && !elementInViewport(element)) {
      element.scrollIntoView({ behavior: "auto" });
    }
  };

  const debounceOnChange = debounce(setAddress, 1000);

  const handleAddressChanged = async (placeId: string) => {
    if (!placeId) {
      setAddress(null);
      return;
    }

    const place = searchResult.find((r) => r.place_id === placeId)!;
    setAddress(place.label);
    
    // If coordinates are not available, fetch them
    if (place.lat === 0 && place.lon === 0) {
      try {
        console.log('App: Getting coordinates for selected place:', placeId);
        const detailedPlace = await googleMapsService.getPlaceDetails(placeId);
        if (detailedPlace) {
          setCenter(`${detailedPlace.lon}|${detailedPlace.lat}`);
          setMarker(`${detailedPlace.lon}|${detailedPlace.lat}`);
          // Update URL with the new coordinates
          updateUrlHash(detailedPlace.lat, detailedPlace.lon);
        }
      } catch (error) {
        console.error('App: Error getting place details:', error);
      }
    } else {
      setCenter(`${place.lon}|${place.lat}`);
      setMarker(`${place.lon}|${place.lat}`);
      // Update URL with the new coordinates
      updateUrlHash(place.lat, place.lon);
    }
  };

  let storedSmsToken  = localStorage.getItem("x-sms-token");
  if (storedSmsToken !== undefined && storedSmsToken !== null && smsToken === undefined && remainingCredits === undefined) {
    const connectFromToken = async (token: string) => {
      const response = await axios.get(
        `https://melcarnebackend.digipulse.ch/connect_from_token?token=${token}`
      );

      if (response.data.status === 'success') {
          setSMSToken(response.data.token); // Save the token
          setRemainingCredits(response.data.remainingCredits);
      }

    };
    connectFromToken(storedSmsToken);
  }

  return (
    <Layout
      id="main-layout-container"
      onClick={() => {
        const mainLayout = document.getElementById("main-layout-container");
        mainLayout && mainLayout.scrollIntoView();
      }}
    >
      {window.self === window.top && (
        <div
          style={{
            width: "100%",
            display: "flex",
            justifyContent: "center",
            backgroundColor: "white",
            padding: "10px 0",
            borderBottom: "1px solid #f0f0f0",
          }}
        >
          <a href="https://transaction-map.ch"><img
            src="https://melcarne.digipulse.ch/logo.svg"
            alt="Melcarne Logo"
            style={{
              height: "100px",
              maxWidth: "200px",
              objectFit: "contain",
            }}
          />
          </a>
        </div>
      )}
      <div
        style={{
          width: "100%",
          display: "flex",
          justifyContent: "center",
          backgroundColor: "white",
        }}
      >
        <div className="layout-header-content">
          <Select
            showSearch
            placeholder="Entrer une adresse"
            defaultActiveFirstOption={false}
            suffixIcon={<SearchOutlined />}
            filterOption={false}
            onSearch={debounceOnChange}
            onClick={() => window.self === window.top && setOpenFilters(false)}
            onChange={handleAddressChanged}
            notFoundContent={null}
            popupMatchSelectWidth={false}
            options={searchResult.map((result: any) => ({
              label: result.display_name,
              value: result.place_id,
            }))}
            allowClear
            value={address}
            style={{ flex: 1, overflow: "hidden" }}
            dropdownStyle={{maxWidth: "95vw"}}
          />

          <Popover
            placement="bottomRight"
            content={
              <Filters
                onApplyFilters={setFilters}
                displayContactUsModal={displayContactUsModal}
                setOpenFilters={setOpenFilters}
              />
            }
            arrow={false}
            open={openFilters}
          >
            <Button
              onClick={() => {
                openTransactionList &&
                  setOpenTransactionList(!openTransactionList);

                setOpenFilters(!openFilters);
              }}
              icon={
                openFilters ? (
                  <DoubleRightOutlined rotate={-90} />
                ) : (
                  <DoubleRightOutlined rotate={90} />
                )
              }
            >
              Filtres
            </Button>
          </Popover>
        </div>
      </div>

      <Modal
        width={ window.self === window.top ? 300 : 600}
        style={{ top: 54 }}
        title="Connexion par SMS"
        open={isSMSModalOpen}
        footer={null}
        onCancel={handleSMSModalCancel}
      >
        <SMSRegisterForm
          setSMSToken={setSMSToken}
          setIsSMSModalOpen={setIsSMSModalOpen}
          setRemainingCredits={setRemainingCredits}
          displayContactUsModal={displayContactUsModal}
          showConfirmForm={showConfirmForm}
          setShowConfirmForm={setShowConfirmForm}
          onSMSConfirmed={() => {
            if (  window.self !== window.top && !openTransactionList) setOpenTransactionList(true);
            if (openFilters) setOpenFilters(false);
          }}
        />
      </Modal>

      <div style={{ fontWeight: "bold" }}>
        {smsToken && <>Crédits restants: {remainingCredits}</>}
      </div>

      <Layout>
        <Drawer
          mask={false}
          placement="left"
          onClose={() => setOpenTransactionList(!openTransactionList)}
          open={openTransactionList}
          title={
            <span
              style={{
                fontSize: 12,
                fontWeight: "bolder",
                color: "#757575",
                letterSpacing: 2,
                width: "100%",
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
              }}
            >{`${parcelles.length} transaction${
              parcelles.length > 1 ? "s" : ""
            }`}</span>
          }
        >
          <List style={{ height: "calc(100% - 64px)", overflow: "auto" }}>
            {loadingParcellesList ? 
              <List.Item style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center'}}>
                <Spin indicator={<LoadingOutlined spin />} size="large" />
              </List.Item> : parcelles.map((parcelle, index) => {
              

              if (parcelle && parcelle.Usages) {
                var clean_usage = parcelle['Usages'];
                if (parcelle['Usages'].includes('Appartement')) {
                  clean_usage = 'Appartement';
                }
                else if (parcelle['Usages'].includes('Habitation à un seul logement')) {
                    clean_usage = 'Habitation à un seul logement';
                }
                else if (parcelle['Usages'].includes('Garage')) {
                  clean_usage = 'Garage';
                }
                else if (parcelle['Usages'].includes('Parking')) {
                  clean_usage = 'Parking';
                }
                else if (parcelle['Usages'].includes('Terrain')) {
                  clean_usage = 'Terrain';
                }
              }

              const fields = sidebarInfo[clean_usage] || defaultFields;
              const noParcelle = parcelle?.NoParcelle;
              const noCommune = parcelle?.NoCommune;

              const searchedParcelle = parcelles.find(
                (p) => p.id === searchLocationAuthenticated
              );

              const labelsNotToBlur = ['Nombre de pièces', 'Etage', 'Année de construction'];
              const blurFilter =
                !(smsToken &&
                searchedParcelle !== undefined &&
                noParcelle === searchedParcelle?.NoParcelle &&
                noCommune === searchedParcelle?.NoCommune) || parcelle.blurred===true
                  ? { filter: "blur(3px)" } 
                  : {};
              const displayNoneFilter =
                (!smsToken && searchedParcelle !== undefined) ||
                (smsToken &&
                  noParcelle === searchedParcelle?.NoParcelle &&
                  noCommune === searchedParcelle?.NoCommune)
                  ? {}
                  : { display: "none" };

              return (
                <List.Item
                  id={noParcelle}
                  key={index}
                  style={{
                    borderRight:
                      noParcelle === selectedParcelle
                        ? "3px solid #001482"
                        : "none",
                    backgroundColor:
                      noParcelle === selectedParcelle ? "#eeeeee" : "#ffffff",
                    cursor: "pointer",
                    ...displayNoneFilter,
                  }}
                  className="parcelle-item"
                  onClick={() => {
                    /*setCenter(
                      `${parcelle["position_sur_map"].coordinates[0]}|${parcelle["position_sur_map"].coordinates[1]}`
                    );*/
                    setSelectedParcelle(noParcelle);
                    setSelectedParcelleNoCommune(noCommune);
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      paddingLeft: 8,
                      paddingRight: 8,
                      width: "100%",
                      fontSize: 12,
                      position: "relative",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        fontWeight: "bolder",
                        fontSize: 13,
                      }}
                    >
                      <span
                        style={{
                          display: "flex",
                          alignItems: "flex-start",
                          textTransform: "uppercase",
                          gap: 8,
                        }}
                      >
                        {displayStatus(parcelle.status === "SVB_Verifiee")}
                        <span
                          title={parcelle.id}
                        >{`${parcelle.Adresse}, ${parcelle.NPA} ${parcelle.CommuneAdresse}`}</span>
                      </span>
                      <span style={{ textAlign: "right", minWidth: 70}}>
                        {parcelle.Year}
                      </span>
                    </div>
                    <div
                      style={{
                        marginLeft: 20,
                        marginBottom: 4,
                        fontSize: 11,
                      }}
                    >
                      {parcelle.Usages}
                    </div>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "flex-end",
                        flexWrap: "wrap",
                      }}
                    >
                      {fields
                        .map((f) => {
                          const value = parcelle[f];

                          const fieldLabelSuffix =
                            searchLocationAuthenticated &&
                            f === "SVB_Verifiee" &&
                            parcelle.Usages?.includes("Appartement")
                              ? " *"
                              : "";

                          if (numbers.includes(f)) {
                            return {
                              label: fieldLabels[f] + fieldLabelSuffix,
                              value: displayNumber(value),
                            };
                          }
                          if (prices.includes(f)) {
                            return {
                              label: fieldLabels[f] + fieldLabelSuffix,
                              value: displayPrice(value),
                            };
                          }

                          return {
                            label: fieldLabels[f] + fieldLabelSuffix,
                            value,
                          };
                        })
                        .filter((d: any) => !!d.value)
                        .map((data: any, index) => (
                          <DisplayField
                            key={index}
                            label={data.label}
                            value={data.value}
                            blurFilter={labelsNotToBlur.includes(data.label) ? {} : blurFilter }
                          />
                        ))}
                    </div>
                    <div
                      onClick={(event) => {
                        event.stopPropagation();
                        if (!parcelle.SVB_Verifiee) return;

                        if (checkedTransactions.includes(parcelle.id))
                          setCheckedTransactions(
                            checkedTransactions.filter((t) => t !== parcelle.id)
                          );
                        else
                          setCheckedTransactions([
                            ...checkedTransactions,
                            parcelle.id,
                          ]);
                      }}
                      style={{
                        position: "absolute",
                        bottom: 0,
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: 12,
                      }}
                    ></div>
                    {searchLocationAuthenticated &&
                    parcelle.Usages?.includes("Appartement") ? (
                      <span style={{ fontStyle: "italic", fontSize: "0.5rem" }}>
                        * Il s'agit de surfaces pondérées
                      </span>
                    ) : (
                      <></>
                    )}
                  </div>
                </List.Item>
              );
            })}
            <List.Item id="last" key="last" style={{}}>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  paddingLeft: 8,
                  paddingRight: 8,
                  width: "100%",
                  fontSize: 12,
                  position: "relative",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    fontWeight: "bolder",
                    fontSize: 13,
                  }}
                >
                  <span
                    style={{
                      textTransform: "uppercase",
                      textAlign: "center",
                      gap: 8,
                      width: "100%",
                    }}
                  >
                    Souhaitez-vous une analyse complète ?
                  </span>
                </div>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    paddingLeft: 8,
                    paddingRight: 8,
                    width: "100%",
                    fontSize: 12,
                    position: "relative",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      fontWeight: "bolder",
                      fontSize: 13,
                      marginTop: "15px",
                    }}
                  >
                    <Button type="primary" onClick={displayContactUsModal}>
                      Contactez-nous
                    </Button>
                  </div>
                </div>
              </div>
            </List.Item>
          </List>
        </Drawer>

        <Content>
          <MapComponent
            center={center}
            marker={marker}
            setLoadingParcellesList={setLoadingParcellesList}
            loadingParcellesList={loadingParcellesList}
            onParcellesChanged={(parcelles) => setParcelles(parcelles)}
            onSelectParcelle={(parcelle: string | null, commune: string | null) => {
              setSelectedParcelle(parcelle);
              setSelectedParcelleNoCommune(commune);
            }}
            parcelleDetails={selectedParcelle}
            parcelleDetailsNoCommune={selectedParcelleNoCommune}
            filters={filters}
            showSMSModal={showSMSModal}
            smsToken={smsToken}
            searchLocationAuthenticated={searchLocationAuthenticated}
            setSearchLocationAuthenticated={setSearchLocationAuthenticated}
            setRemainingCredits={setRemainingCredits}
            displayPrice={displayPrice}
            displayContactUsModal={displayContactUsModal}
            remainingCredits={remainingCredits}
            onParcelleDetailsAccess={() => {
              setOpenFilters(false);
            }}
            openTransactionList={openTransactionList}
            setOpenTransactionList={setOpenTransactionList}
            setOpenFilters={setOpenFilters}
            recenterMap={recenterMap}
          />
        </Content>
        <Sider
          style={{
            backgroundColor: "white",
            height: "calc(100vh - 64px)",
            overflow: "auto",
            display: "none",
          }}
          width={350}
        >
          <Filters
            onApplyFilters={setFilters}
            displayContactUsModal={displayContactUsModal}
            setOpenFilters={setOpenFilters}
          />
        </Sider>
        <Button
          style={{
            position: "absolute",
            marginTop: 5,
            zIndex: 1,
            left: 4,
          }}
          onClick={() => {
            setRecenterMap(!recenterMap);
          }}
          icon={
            <DoubleRightOutlined />
          }
        >
          Recentrer
        </Button>
      </Layout>
    </Layout>
  );
}

export default App;

const DisplayField: FC<{ label: string; value: any; blurFilter: any }> = ({
  label,
  value,
  blurFilter,
}) => {
  return (
    <div
      style={{
        width: "32%",
        marginBottom: 4,
        textAlign: "end",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <span style={{ color: "#757575", fontSize: 9 }}>{label}</span>
      <span style={{ fontSize: 12, ...blurFilter }}>{value}</span>
    </div>
  );
};
